import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getContactFullName } from '~/utils/phoneHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { CallQueueItem } from '@mangovoice/types'

import { CallItemHeader } from './CallItemHeader'
import { PersonCard } from './PersonCard'

interface CallListProps {
  callItems: CallQueueItem[]
}

export const CallList: React.FC<CallListProps> = React.memo(
  ({ callItems }) => {
    const { t } = useTranslation()
    const [expandedCallId, setExpandedCallId] = useState<string | null>(
      callItems.length > 0 ? callItems[0]._id : null,
    )

    const handleToggle = (callId: string) => {
      setExpandedCallId(expandedCallId === callId ? null : callId)
    }

    const hasMultipleCalls = callItems.length > 1

    // Auto-expand when only one call remains
    useEffect(() => {
      if (callItems.length === 1 && expandedCallId !== callItems[0]._id) {
        setExpandedCallId(callItems[0]._id)
      }
    }, [callItems, expandedCallId])

    if (callItems.length === 0) {
      return (
        <Text paddingTop={2} {...textStyles[textStyleKeys.Body3]}>
          {t('popup.emptyStates.noActiveCalls')}
        </Text>
      )
    }

    return (
      <VStack gap={3} width="100%">
        {callItems.map((callItem) => {
          const isExpanded = expandedCallId === callItem._id

          return (
            <Box key={callItem._id} width="100%">
              {isExpanded ? (
                <PersonCard
                  callItem={callItem}
                  onCollapse={
                    hasMultipleCalls
                      ? () => handleToggle(callItem._id)
                      : undefined
                  }
                />
              ) : (
                <Flex
                  as="button"
                  onClick={() => handleToggle(callItem._id)}
                  width="100%"
                  p={4}
                  border="1px solid"
                  borderColor="border.muted"
                  borderRadius="md"
                  bg="bg.panel"
                  cursor="pointer"
                  _hover={{
                    bg: 'bg.default',
                    borderColor: 'brand.focusRing',
                  }}
                  transition="background-color 0.2s"
                >
                  <CallItemHeader callItem={callItem} isExpanded={isExpanded} />
                </Flex>
              )}
            </Box>
          )
        })}
      </VStack>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent re-renders when call data is the same
    if (prevProps.callItems.length !== nextProps.callItems.length) {
      return false
    }

    // Compare each call item
    for (let i = 0; i < prevProps.callItems.length; i++) {
      const prevCall = prevProps.callItems[i]
      const nextCall = nextProps.callItems[i]

      if (
        prevCall?.callUuid !== nextCall?.callUuid ||
        prevCall?.name !== nextCall?.name ||
        prevCall?.number !== nextCall?.number ||
        prevCall?.status !== nextCall?.status ||
        getContactFullName(prevCall?.contactInfo) !==
          getContactFullName(nextCall?.contactInfo) ||
        prevCall?.isOnHold !== nextCall?.isOnHold ||
        prevCall?.isMuted !== nextCall?.isMuted
      ) {
        return false
      }
    }

    return true
  },
)
